import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hasSuperAdminAccess } from '../hooks/Auth';
import routes from '../routes';
import useGlobalPermissions from '../useGlobalPermissions';
import { checkIfUserAdminOrSuperAdmin } from '../utils/roles';
import { SAM_CHATBOT_FLAG } from '../config/env';
import { canAccessModule } from '../utils/moduleAccess';
import { rememberPostLoginPath } from '../utils/postLoginRedirect';

const Loader = React.lazy(() => import('../components/loader/Loader'));
const AppLogout = React.lazy(() => import('../AppLogout'));
const Chatbot = React.lazy(() => import('../components/chatbot/Chatbot'));
const Layout = React.lazy(() => import('../components/layout/Index'));

function ProtectedRoutes({
  Component,
  requiredPermission = '',
  path = '',
  requireSuperAdmin = false,
  requireSuperAdminOrDomainAdmin = false,
}) {
  const {
    moduleAccessPermissions = {},
    permissionsLoaded,
    dataAccessPermissions = {},
    userData,
  } = useGlobalPermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const localType = typeof window !== 'undefined' ? localStorage.getItem('type') : null;

  const userDomains = userData?.data?.userDomains?.map((d) => d?.domainName) ?? [];

  const isSuperAdmin =
    hasSuperAdminAccess(userDomains) ||
    userData?.data?.role === 'SuperAdmin' ||
    localType === 'superAdmin';

  const userForRoleCheck = userData?.data;
  const hasSuperAdminOrDomainAdminAccess = checkIfUserAdminOrSuperAdmin(userForRoleCheck);

  const effectivePath = path || location.pathname;

  const hasRouteAccess = useMemo(
    () =>
      canAccessModule({
        moduleAccessPermissions,
        dataAccessPermissions,
        requiredPermission,
        dataAccessPath: effectivePath,
      }),
    [requiredPermission, effectivePath, moduleAccessPermissions, dataAccessPermissions]
  );

  const [fallbackReady, setFallbackReady] = useState(false);
  const watchdogTimerRef = useRef(null);

  const derivedEnoughData =
    token &&
    (Object.keys(moduleAccessPermissions || {}).length > 0 ||
      Object.keys(dataAccessPermissions || {}).length > 0 ||
      !!userData?.data);

  useEffect(() => {
    if (permissionsLoaded) {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
      setFallbackReady(false);
      return undefined;
    }
    if (derivedEnoughData && !fallbackReady) {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = setTimeout(() => {
        setFallbackReady(true);
      }, 1200);
    }
    return () => {
      if (watchdogTimerRef.current) clearTimeout(watchdogTimerRef.current);
    };
  }, [permissionsLoaded, derivedEnoughData, fallbackReady]);

  const authState = useMemo(() => {
    if (!token) return { state: 'no-token' };
    if (!permissionsLoaded && !fallbackReady) return { state: 'loading' };
    if (requireSuperAdmin && !isSuperAdmin) return { state: 'forbidden' };
    if (requireSuperAdminOrDomainAdmin && !hasSuperAdminOrDomainAdminAccess)
      return { state: 'forbidden' };
    if (isSuperAdmin) return { state: 'ok' };
    if (!hasRouteAccess) return { state: 'forbidden' };
    return { state: 'ok' };
  }, [
    token,
    permissionsLoaded,
    fallbackReady,
    isSuperAdmin,
    hasSuperAdminOrDomainAdminAccess,
    requireSuperAdmin,
    requireSuperAdminOrDomainAdmin,
    hasRouteAccess,
  ]);

  useEffect(() => {
    if (!token) {
      rememberPostLoginPath(location);
      navigate('/login', { replace: true, state: { from: location } });
    }
  }, [token, navigate, location]);

  if (!token) return null;

  if (authState.state === 'loading') {
    return <Loader />;
  }

  if (authState.state === 'forbidden') {
    if (location.pathname !== routes.HomePage) {
      navigate(routes.HomePage, { replace: true });
      return null;
    }
    return null;
  }

  const isChatbotEnabled = SAM_CHATBOT_FLAG && moduleAccessPermissions?.sam !== false;

  return (
    <AppLogout>
      <Layout>
        <Component />
        {isChatbotEnabled && <Chatbot />}
      </Layout>
    </AppLogout>
  );
}

ProtectedRoutes.propTypes = {
  Component: PropTypes.elementType.isRequired,
  requiredPermission: PropTypes.string,
  path: PropTypes.string,
  requireSuperAdmin: PropTypes.bool,
  requireSuperAdminOrDomainAdmin: PropTypes.bool,
};

export default ProtectedRoutes;
